import { List, TextInput, ReferenceInput, SelectInput, Datagrid, DateInput, FunctionField, Button, SimpleList } from "react-admin";
import { administrationRequests } from "api/administrationApi";
import { useNavigate } from "react-router";
import { EmployeeServicePath, BUSINESS_TRIPS, STAFF_REQUEST, StaffRequestTypes, MOBILE_BREAKPOINT, StaffRequestStatuses } from "utils/constants";
import TravelersField from "server/components/Fields/TravelersField";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, useMediaQuery } from "@mui/material";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import { EmptyList } from "server/components";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";
import { getUser } from "auth/security/securityUtils";
import { employeeRequests } from "api/employeeApi";
import { toDateOnly } from "utils/helpers";
import CancelRequestDialog from "../components/CancelRequestDialog";
import AttachFileDialog from "../components/AttachFileDialog";
import { tripColumns, tripMobileRows } from "./tripColumns";

// A final report is only due once the owner's approved trip has actually ended.
const canAttachReport = (rec, userId) =>
  rec?.createdByUserId === userId &&
  rec?.staffRequest?.status === StaffRequestStatuses.Approved &&
  toDateOnly(rec?.dateTo) <= toDateOnly(new Date().toISOString());

const filters = [
  <TextInput
    label="Име"
    source="name"
    alwaysOn
  />,
  <ReferenceInput
    source="specialtyId"
    reference={administrationRequests.getSpecialitiesSelect()}
    label="Факултет"
    alwaysOn
  >
    <SelectInput
      label="Факултет"
      emptyText="Всички"
      style={{ minWidth: 280 }}
    />
  </ReferenceInput>,
  <ReferenceInput
    source="department"
    reference={administrationRequests.getDepartmentsSelect()}
    label="Катедра"
    alwaysOn
  >
    <SelectInput
      label="Катедра"
      emptyText="Всички"
      alwaysOn
    />
  </ReferenceInput>,
  <DateInput
    label="От дата"
    source="startDate"
    alwaysOn
  />,
  <DateInput
    label="До дата"
    source="endDate"
    alwaysOn
  />,
];

const RowActions = ({ rec, navigate, userId, iconOnly = false }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      alignItems: "center",
      justifyItems: "center",
    }}
  >
    <Box>
      <Button
        label={iconOnly ? "" : "Детайли"}
        onClick={() =>
          navigate(`/${EmployeeServicePath}details/${STAFF_REQUEST}/${rec?.id}`, {
            state: { type: StaffRequestTypes.BusinessTrip, readOnly: true },
          })
        }
      >
        <VisibilityIcon />
      </Button>
    </Box>
    <Box>
      {canAttachReport(rec, userId) && (
        <SecuredComponent permission={permissions.canUpdateLeaveRequests}>
          <AttachFileDialog
            url={employeeRequests.uploadBusinessTripReportFile(rec?.id)}
            title={`Прикачване на отчет към командировка №${rec?.id}`}
            label="Отчет от командировка"
            buttonLabel="Отчет"
            placeholderText="Прикачи отчет"
            iconOnly={iconOnly}
          />
        </SecuredComponent>
      )}
    </Box>
    <Box>
      {rec?.createdByUserId === userId && rec?.staffRequest?.status === StaffRequestStatuses.Pending && (
        <SecuredComponent permission={permissions.canUpdateLeaveRequests}>
          <Button
            label={iconOnly ? "" : "Редактиране"}
            onClick={() => navigate(`/${EmployeeServicePath}edit/${BUSINESS_TRIPS}/${rec?.id}`)}
          >
            <EditIcon />
          </Button>
        </SecuredComponent>
      )}
    </Box>
    <Box>
      <SecuredComponent permission={permissions.canUpdateLeaveRequests}>
        {rec?.createdByUserId === userId && (
          <CancelRequestDialog
            record={rec}
            requestType={StaffRequestTypes.BusinessTrip}
            iconOnly={iconOnly}
          />
        )}
      </SecuredComponent>
    </Box>
  </Box>
);

const MobileView = ({ navigate, userId }) => (
  <SimpleList
    empty={<EmptyList />}
    linkType={false}
    sx={settings.style.mobileList}
    primaryText={() => (
      <MobileListRow fields={[{ label: "Именa", value: <TravelersField /> }]} />
    )}
    secondaryText={tripMobileRows}
    tertiaryText={rec => (
      <RowActions
        rec={rec}
        navigate={navigate}
        userId={userId}
        iconOnly
      />
    )}
  />
);

const DesktopView = ({ navigate, userId }) => (
  <Datagrid>
    {tripColumns()}
    <FunctionField
      label="Действия"
      render={rec => (
        <RowActions
          rec={rec}
          navigate={navigate}
          userId={userId}
        />
      )}
    />
  </Datagrid>
);

const BusinessTripList = () => {
  const navigate = useNavigate();
  const userId = JSON.parse(getUser())?.userId;
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  return (
    <List
      title="Командировки"
      filters={filters}
      exporter={false}
    >
      {isMobile ? (
        <MobileView
          navigate={navigate}
          userId={userId}
        />
      ) : (
        <DesktopView
          navigate={navigate}
          userId={userId}
        />
      )}
    </List>
  );
};

export default BusinessTripList;
